import { useContext, useEffect, useState, useRef } from 'react';
import { AppContext } from '../../../App.context';
import { ApiContext } from '../../../utils/networking/Api.context';
import { capitalize } from '../../../utils/TextUtils';

const useHotelSelectorHook = (onHotelSelected, enableAllHotels, autoSelect, isMultiple = false) => {
  const { appState, appActions } = useContext(AppContext);
  const { API } = useContext(ApiContext);
  const initialSelectDone = useRef(false);

  const [state, setState] = useState({
    hotels: [],
    hotelList: [],
    selectedValue: isMultiple ? [] : null,
    currentlySelectedHotel: appActions.getSelectedHotel(),
    selectedHotelLabel: null,
    loadedHotels: false,
  });

  function updateState(object) {
    setState(prev => ({ ...prev, ...object }));
  }

  function parseHotelList(list) {
    const newList = [...list];
    if (enableAllHotels) {
      newList.unshift({ id: null, name: appActions.translate('hotelSelector.allHotels') });
    }
    return newList;
  }

  useEffect(() => {
    if (!appState.hotelList) {
      API.allHotelsRequest()
        .then(res => {
          appActions.setHotelList(res);
          const parsedList = parseHotelList(JSON.parse(JSON.stringify(res)));
          updateState({ hotels: parsedList, hotelList: parsedList });
        })
        .catch(console.warn);
    } else {
      const parsedList = parseHotelList(JSON.parse(JSON.stringify(appState.hotelList)));
      updateState({ hotels: parsedList, hotelList: parsedList });
    }
  }, []);

  useEffect(() => {
    if (state.hotels?.length && !initialSelectDone.current) {
      let hotelToSelect = appActions.getSelectedHotel();

      if (!hotelToSelect && autoSelect) {
        hotelToSelect = actions.getFirstHotel();
        appActions.setSelectedHotel(hotelToSelect);
      }

      if (hotelToSelect) {
        updateState({
          selectedValue: isMultiple
            ? [hotelToSelect.id || hotelToSelect.key || null]
            : hotelToSelect.id || hotelToSelect.key || null,
          selectedHotelLabel: hotelToSelect.value || hotelToSelect.name || null,
        });

        actions.setCurrentlySelectedHotel(hotelToSelect);

        if (autoSelect && onHotelSelected) {
          if (isMultiple) {
            onHotelSelected([
              {
                hotelId: hotelToSelect.id,
                name: hotelToSelect.value || hotelToSelect.name,
                slug: hotelToSelect.slug,
              },
            ]);
          } else {
            onHotelSelected(hotelToSelect);
          }
        }
      } else {
        updateState({
          selectedValue: isMultiple ? [] : null,
          selectedHotelLabel: null,
        });
      }

      initialSelectDone.current = true;
    }
  }, [state.hotels]);

  const actions = {
    getDefaultValue: () => {
      if (appActions.getSelectedHotel()) {
        return capitalize(appActions.getSelectedHotel()?.value);
      }
      if (enableAllHotels) return appActions.translate('hotelSelector.allHotels');
      if (appState?.hotelList?.length) {
        return capitalize(appState?.hotelList[0].name);
      }
      return null;
    },

    setCurrentlySelectedHotel: hotel => {
      updateState({ currentlySelectedHotel: hotel });
    },

    getFirstHotel: () => {
      const hotel = appState?.hotelList?.[0] || state?.hotels?.[0] || {};
      return {
        id: hotel.id || null,
        key: hotel.id || null,
        value: hotel.name || '',
        slug: hotel.slug,
      };
    },

    handleChange: value => {
      if (isMultiple) {
        if (!Array.isArray(value)) return;

        updateState({ selectedValue: value });

        if (onHotelSelected) {
          const selectedHotels = state.hotels
            .filter(h => value.includes(h.id === null ? 'null' : h.id))
            .map(h => ({ hotelId: h.id, hotelName: h.name }));

          onHotelSelected(selectedHotels);
        }
      } else {
        const selectedId = value === 'null' ? null : value;

        const hotel =
          state.hotels.find(h => h.id === selectedId) ||
          (selectedId === null
            ? { id: null, name: appActions.translate('hotelSelector.allHotels') }
            : null);

        if (!hotel) return;

        const hotelObj = { hotelId: hotel.id, hotelName: hotel.name };

        updateState({ selectedValue: selectedId, selectedHotelLabel: hotel.name });

        if (onHotelSelected) onHotelSelected(hotelObj);
        actions.setCurrentlySelectedHotel(hotelObj);
        appActions.setSelectedHotel({
          ...hotelObj,
          key: hotelObj.hotelId,
          value: hotelObj.hotelName,
        });
      }
    },

    onSelectNewHotel: value => {
      const currentHotels = Array.isArray(state.selectedValue) ? state.selectedValue : [];

      if (value === 'null') {
        updateState({ selectedValue: ['null'] });
        if (onHotelSelected) {
          onHotelSelected([
            { hotelId: null, hotelName: appActions.translate('hotelSelector.allHotels') },
          ]);
        }
        return;
      }

      if (currentHotels.includes('null')) {
        const updated = [value];

        const userSelectedHotel = appActions.getSelectedHotel()?.id || null;

        if (value !== userSelectedHotel && userSelectedHotel !== null) {
          updated.push(userSelectedHotel);
        }

        updateState({ selectedValue: updated });

        if (onHotelSelected) {
          const selectedHotels = state.hotels
            .filter(h => updated.includes(h.id === null ? 'null' : h.id))
            .map(h => ({ hotelId: h.id, hotelName: h.name }));

          onHotelSelected(selectedHotels);
        }
        return;
      }

      const updatedHotels = Array.from(new Set([...currentHotels, value]));

      updateState({ selectedValue: updatedHotels });

      if (onHotelSelected) {
        const selectedHotels = state.hotels
          .filter(h => updatedHotels.includes(h.id === null ? 'null' : h.id))
          .map(h => ({ hotelId: h.id, hotelName: h.name }));

        onHotelSelected(selectedHotels);
      }
    },

    onDeselectNewHotel: value => {
      const currentHotels = Array.isArray(state.selectedValue) ? state.selectedValue : [];

      if (currentHotels.length === 1 && currentHotels[0] === value) {
        return;
      }

      if (value === 'null') {
        const filtered = currentHotels.filter(h => h !== 'null');
        updateState({ selectedValue: filtered });

        if (onHotelSelected) {
          const selectedHotels = state.hotels
            .filter(h => filtered.includes(h.id === null ? 'null' : h.id))
            .map(h => ({ hotelId: h.id, hotelName: h.name }));

          onHotelSelected(selectedHotels);
        }
        return;
      }

      const filtered = currentHotels.filter(h => h !== value);

      updateState({ selectedValue: filtered });

      if (onHotelSelected) {
        const selectedHotels = state.hotels
          .filter(h => filtered.includes(h.id === null ? 'null' : h.id))
          .map(h => ({ hotelId: h.id, hotelName: h.name }));

        onHotelSelected(selectedHotels);
      }
    },
  };

  return { state, actions };
};

export default useHotelSelectorHook;
